import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Briefcase, CheckCircle2, Clock, TrendingUp } from 'lucide-react'
import type { MyCaseStatistics } from '../../../types/dashboard'
import { StatCard } from './stat-card'
import { BreakdownBar } from './breakdown-bar'
import { PRIORITY_COLOURS, STATUS_COLOURS } from './summary-view.colours'

interface WelcomeProps {
  userName: string
  t: TFunction<'admin'>
}

export function Welcome({ userName, t }: WelcomeProps): ReactElement {
  return (
    <div>
      <h2 className="text-xl font-semibold" style={{ color: 'var(--text)' }}>
        {t('summary.welcomeBack', { name: userName })}
      </h2>
      <p className="mt-1" style={{ color: 'var(--text-muted)' }}>
        {t('summary.performanceSummary')}
      </p>
    </div>
  )
}

interface StatsGridProps {
  statistics: MyCaseStatistics
  t: TFunction<'admin'>
}

export function StatsGrid({ statistics, t }: StatsGridProps): ReactElement {
  const avg = statistics.avgDaysToClose !== null ? `${statistics.avgDaysToClose.toString()}d` : '-'
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title={t('summary.totalCases')}
        value={statistics.totalCases}
        icon={Briefcase}
        color="accent"
      />
      <StatCard
        title={t('summary.activeCases')}
        value={statistics.activeCases}
        icon={Clock}
        color="blue"
      />
      <StatCard
        title={t('summary.closedThisMonth')}
        value={statistics.closedThisMonth}
        icon={CheckCircle2}
        color="amber"
      />
      <StatCard title={t('summary.avgDaysToClose')} value={avg} icon={TrendingUp} color="purple" />
    </div>
  )
}

interface BreakdownsProps {
  statistics: MyCaseStatistics
  t: TFunction<'admin'>
}

export function Breakdowns({ statistics, t }: BreakdownsProps): ReactElement {
  const statusLabels = {
    collection: t('summary.statusCollection'),
    analysis: t('summary.statusAnalysis'),
    review: t('summary.statusReview'),
    reporting: t('summary.statusReporting'),
    closed: t('summary.statusClosed'),
    archived: t('summary.statusArchived'),
  }
  const priorityLabels = {
    critical: t('summary.priorityCritical'),
    high: t('summary.priorityHigh'),
    medium: t('summary.priorityMedium'),
    low: t('summary.priorityLow'),
  }
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <BreakdownPanel title={t('summary.casesByStatus')}>
        <BreakdownBar
          data={statistics.casesByStatus}
          colors={STATUS_COLOURS}
          labels={statusLabels}
        />
      </BreakdownPanel>
      <BreakdownPanel title={t('summary.casesByPriority')}>
        <BreakdownBar
          data={statistics.casesByPriority}
          colors={PRIORITY_COLOURS}
          labels={priorityLabels}
        />
      </BreakdownPanel>
    </div>
  )
}

interface BreakdownPanelProps {
  title: string
  children: ReactElement
}

function BreakdownPanel({ title, children }: BreakdownPanelProps): ReactElement {
  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: 'var(--card-border)', background: 'var(--bg-card)' }}
    >
      <h3 className="mb-4 font-medium" style={{ color: 'var(--text)' }}>
        {title}
      </h3>
      {children}
    </div>
  )
}

interface StateMessageProps {
  message: string
  spinner?: boolean
}

export function StateMessage({ message, spinner }: StateMessageProps): ReactElement {
  return (
    <div className="flex items-center justify-center py-20">
      {spinner ? (
        <div className="flex flex-col items-center gap-3">
          <div className="db-spinner h-8 w-8 animate-spin rounded-full border-4" />
          <p style={{ color: 'var(--text-muted)' }}>{message}</p>
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)' }}>{message}</p>
      )}
    </div>
  )
}
